import firebase from "../Firebase";
import { agent } from "./agent";
import * as API from "./apiPath";
import { API_BASE_URL } from "./apiBase";

const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (e) {
    return "https://vlookup.ai";
  }
})();

export function normalizeChatImageUrl(url) {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("/static/") ||
    trimmed.startsWith("/revamp/")
  ) {
    return trimmed;
  }
  if (trimmed.startsWith("/")) {
    return `${API_ORIGIN}${trimmed}`;
  }
  return `${API_ORIGIN}/api/public/upload/user/${trimmed}`;
}

const str = (id) => (id == null || id === "" ? "" : String(id));

export function profileImageOf(user) {
  if (!user || typeof user !== "object") return "";
  const url =
    user.image ||
    user.user_image ||
    user.mentors_image ||
    user.mentor_image ||
    user.mentee_image ||
    "";
  if (typeof url !== "string" || !url) return "";
  return normalizeChatImageUrl(url);
}

export function bustChatImage(rawUrl, version) {
  if (!rawUrl || typeof rawUrl !== "string") return rawUrl || "";
  if (rawUrl.startsWith("data:") || rawUrl.startsWith("blob:") || rawUrl.startsWith("/static")) {
    return rawUrl;
  }
  const url = normalizeChatImageUrl(rawUrl);
  const base = url.replace(/[?&]v=\d+/g, "").replace(/\?&/, "?").replace(/\?$/, "");
  const token = version || Date.now();
  return base + (base.includes("?") ? "&" : "?") + "v=" + token;
}

export function chatAvatarImage(avatars, userId, fallback) {
  const id = str(userId);
  if (!id) return fallback || "";
  const live = avatars && (avatars[id] || avatars[userId]);
  const url = live && (typeof live === "string" ? live : live.image);
  return (typeof url === "string" && url) || fallback || "";
}

function writeRoomImages(snap, id, imageUrl, stamp) {
  const updates = {};
  snap.forEach((roomSnap) => {
    const room = roomSnap.val() || {};
    const roomKey = roomSnap.key;
    if (!roomKey) return;
    const mentorId = str(room.mentor_id);
    const menteeId = str(room.mentee_id);
    let field = "";
    if (mentorId === id) field = "mentor_image";
    else if (menteeId === id) field = "mentee_image";
    if (!field) return;
    updates["users/" + id + "/" + roomKey + "/" + field] = imageUrl;
    if (stamp) {
      updates["users/" + id + "/" + roomKey + "/" + field + "_at"] = stamp;
    }
    const otherId = mentorId === id ? menteeId : mentorId;
    if (otherId) {
      updates["users/" + otherId + "/" + roomKey + "/" + field] = imageUrl;
      if (stamp) {
        updates["users/" + otherId + "/" + roomKey + "/" + field + "_at"] = stamp;
      }
    }
  });
  return updates;
}

export function syncChatProfileImage(userId, imageUrl) {
  if (!userId || !imageUrl) return Promise.resolve();
  const id = str(userId);
  const stamp = Date.now();
  const root = firebase.database().ref();
  return firebase
    .database()
    .ref("users/" + id)
    .once("value")
    .then((snap) => {
      const roomUpdates = writeRoomImages(snap, id, imageUrl, stamp);
      return root.update(roomUpdates).then(() =>
        root
          .update({
            ["avatars/" + id + "/image"]: imageUrl,
            ["avatars/" + id + "/updated"]: stamp,
          })
          .catch(() => {})
      );
    })
    .catch(() => {});
}

export async function fetchUserProfileImage(userId) {
  const id = str(userId);
  if (!id) return "";
  try {
    const res = await agent.get(API.GETUSERDATA + "/" + id);
    return profileImageOf(res?.data?.data);
  } catch (e) {
    return "";
  }
}

function rowsFrom(res) {
  const body = res?.data?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
}

async function fetchRelationshipImages(roleId, userId) {
  const map = {};
  const remember = (id, url) => {
    if (str(id) && url) map[str(id)] = url;
  };
  try {
    if (roleId === "Mentor") {
      const current = await agent.get(API.LOAD_ACCEPTED_MENTEES_DATA, {
        params: { page: 1 },
      });
      rowsFrom(current).forEach((row) => {
        remember(row.mentee_id, row.mentee_image || row.image);
        remember(row.mentor_id, row.mentors_image || row.mentor_image || row.image);
      });
    } else if (userId) {
      const mine = await agent.get(API.LOAD_MY_MENTORS + "/" + userId, {
        params: { page: 1 },
      });
      rowsFrom(mine).forEach((row) => {
        remember(row.mentee_id, row.mentee_image || row.image);
        remember(row.mentor_id, row.mentors_image || row.mentor_image || row.image);
      });
    }
  } catch (e) {}
  return map;
}

export async function hydrateChatAvatars(userIds, rooms = [], opts = {}) {
  const ids = new Set();
  (userIds || []).forEach((id) => {
    if (str(id)) ids.add(str(id));
  });
  (rooms || []).forEach((room) => {
    if (room?.mentor_id) ids.add(str(room.mentor_id));
    if (room?.mentee_id) ids.add(str(room.mentee_id));
  });
  const urls = {};
  await Promise.all(
    [...ids].map(async (id) => {
      const image = await fetchUserProfileImage(id);
      if (image) urls[id] = image;
    })
  );
  const missing = [...ids].filter((id) => !urls[id]);
  if (missing.length) {
    const listed = await fetchRelationshipImages(opts.roleId, opts.userId);
    missing.forEach((id) => {
      if (listed[id]) urls[id] = listed[id];
    });
  }
  const map = {};
  const stamp = Date.now();
  Object.keys(urls).forEach((id) => {
    map[id] = { image: urls[id], updated: stamp };
    syncChatProfileImage(id, urls[id]);
  });
  return map;
}
